import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { TreeNode } from "primereact/treenode";
import { TreeSelect, TreeSelectChangeEvent } from "primereact/treeselect";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import "../dashboard-styles/DashboardMain.scss";
import { getFlatNodeMap, getMenuItems, getTreeSelectOptions } from "../utils";

interface DashboardTabsDropdownMenuProps {
  isMobile: boolean;
  menuRef: React.RefObject<Menu | null>;
  tabId: number;
  dashboard: Dashboard;
}

enum ArrowDirection {
  UP = "up",
  DOWN = "down",
}

type NodeKey = Record<string, boolean>

const DashboardTabsDropdownMenu = ({
  isMobile,
  menuRef,
  tabId,
  dashboard,
}: DashboardTabsDropdownMenuProps) => {
  const [filterValue, setFilterValue] = useState<string>("");

  const navigate = useNavigate();

  const selectedTabUUID = useMemo(() => {
    const currentTab = dashboard?.dashboardTabsJs?.[tabId];
    return currentTab ? currentTab.uuid : null;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const handleTabChange = (
    tabUUID: string,
    e?: TreeSelectChangeEvent | { originalEvent?: Event }
  ) => {
    const selectedIndex = dashboard.dashboardTabsJs.findIndex(tab => tab.uuid === tabUUID);

    const isCtrlOrMetaPressed =
      e?.originalEvent &&
      ("ctrlKey" in e.originalEvent || "metaKey" in e.originalEvent) &&
      ((e.originalEvent as MouseEvent).ctrlKey || (e.originalEvent as MouseEvent).metaKey);

    if (!isMobile && isCtrlOrMetaPressed) {
      const newTabUrl = `${window.location.pathname}/#/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      window.open(newTabUrl, "_blank");
    } else {
      const url = `/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      navigate(url);
    }
  };

  const changeTabWithArrow = (direction: ArrowDirection) => {
    const newTabIndex = direction === ArrowDirection.DOWN ? tabId + 1 : tabId - 1;
    if (newTabIndex < 0 || newTabIndex >= dashboard.dashboardTabsJs.length) return;
    handleTabChange(dashboard.dashboardTabsJs[newTabIndex].uuid);
  };

  const treeOptions = useMemo(
    () => getTreeSelectOptions(dashboard.dashboardTabsJs, dashboard.tabGroups ?? []),
    [dashboard.dashboardTabsJs, dashboard.tabGroups]
  );

  const expandedKeys = useMemo(() => {
    const keys: NodeKey = {};
    treeOptions.forEach(node => {
      if ("children" in node && node.children?.length) {
        keys[node.key] = true;
      }
    });
    return keys;
  }, [treeOptions]);

  const flatNodeMap = useMemo(() => getFlatNodeMap(treeOptions), [treeOptions]);

  const handleTreeSelectChange = (
    e: TreeSelectChangeEvent,
    currentFlatNodeMap: Record<string, TreeNode>
  ) => {
    const selectedValue = e.value as string;
    const selectedNode = currentFlatNodeMap[selectedValue];

    if (!selectedNode || selectedNode.children) return;
    handleTabChange(selectedValue, e);
  };

  const nodeTemplate = (node: TreeNode) => {
    const isLeaf = !node.children;

    return (
      <span
        onClick={e => {
          if (!isLeaf) e.stopPropagation();
        }}
        className={`inline-block h-2rem line-height-4 w-full font-${isLeaf ? "normal" : "bold"}`}
      >
        {node.label}
      </span>
    );
  };

  const valueTemplate = (node: TreeNode | TreeNode[]) => {
    return Array.isArray(node) ? node[0].label : node.label;
  };

  return (
    <div>
      {isMobile ? (
        <Menu
          ref={menuRef}
          model={getMenuItems(
            dashboard.dashboardTabsJs,
            dashboard.tabGroups ?? [],
            handleTabChange
          )}
          popup
          className="w-12 menu-list"
        />
      ) : (
        <div className="flex flex-row gap-2 items-center tabs-dropdown">
          <TreeSelect
            value={selectedTabUUID}
            options={treeOptions}
            onChange={(e: TreeSelectChangeEvent) => handleTreeSelectChange(e, flatNodeMap)}
            className="w-20rem"
            filter
            filterValue={filterValue}
            onFilterValueChange={e => setFilterValue(e.value)}
            onHide={() => setFilterValue("")}
            placeholder={T("action.select-tab")}
            selectionMode="single"
            valueTemplate={(node: TreeNode | TreeNode[]) => valueTemplate(node)}
            nodeTemplate={nodeTemplate}
            expandedKeys={expandedKeys}
          />
          {dashboard.showTabNavigation && (
            <>
              <Button
                icon="pi pi-arrow-left"
                className="p-button-text"
                disabled={tabId === 0}
                tooltip={T("action.select-previous-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() => changeTabWithArrow(ArrowDirection.UP)}
              />
              <Button
                icon="pi pi-arrow-right"
                className="p-button-text"
                disabled={tabId === dashboard.dashboardTabsJs.length - 1}
                tooltip={T("action.select-next-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() => changeTabWithArrow(ArrowDirection.DOWN)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardTabsDropdownMenu;
